import { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleOutline } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
import { onRefresh } from '@/components/Error/utils';

interface Props {
  /**
   * @deprecated Transient disconnects no longer unmount children / show a loader.
   * Kept so existing call sites that pass `loader` continue to typecheck.
   */
  loader?: React.ReactNode;
  children: (props: { onWebsocketDisconnect: VoidFunction }) => React.ReactNode;
}

/**
 * Hosts collab-editor trees. Soft WS drops are recovered in-place by y-websocket /
 * useYWebsocketProvider — this container must NOT unmount children on a single
 * disconnect (that remount was the "page refresh" UX).
 *
 * `onWebsocketDisconnect` is reserved for sustained reconnect failure, after which
 * we show a reload EmptyState.
 */
const WebsocketContainer = (props: Props) => {
  const { t } = useTranslation();
  const [hasFailed, setHasFailed] = useState(false);
  const { children } = props;

  const onWebsocketDisconnect = useCallback(() => {
    console.info('[WS] WebsocketContainer: sustained reconnect failure');
    setHasFailed(true);
  }, []);

  if (hasFailed) {
    return (
      <EmptyState
        Icon={AlertTriangleOutline}
        className="h-full"
        description={t('Common:Error.DefaultError.description')}
        primaryAction={{ label: t('Common:Error.Reload'), onClick: onRefresh }}
        title={t('Common:Error.DefaultError.heading')}
        width="narrow"
      />
    );
  }

  return children({ onWebsocketDisconnect });
};

export default WebsocketContainer;
